import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  Menu,
  X,
  Twitter,
  Linkedin,
  ArrowRight,
  Clock,
  BookOpen,
  Lightbulb,
  TrendingUp,
  Building2,
  Sprout,
  Beer,
  UtensilsCrossed,
  Cpu,
  Mail,
  CheckCircle,
  AlertCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { TerraMatrixBrand } from "@/components/branding/TerraMatrixBrand";
import { useResponsiveInfo } from "@/hooks/useResponsiveViewport";
import { SignInModal } from "@/components/auth/SignInModal";
import { PilotContactModal, ThankYouModal } from "@/components/pilot";
import { DemoModal } from "@/components/demo";
import { newsletterService } from "@/services/newsletterService";
import { SEO } from "@/components/seo/SEO";

// ============================================================================
// DESIGN TOKENS (Style Guide Colors)
// ============================================================================
const colors = {
  primary: "hsl(var(--primary))",
  primaryHover: "hsl(var(--primary) / 0.9)",
  secondary: "#B2AC88", // Sage Green
  seafoam: "#93E9BE",
  blueGrotto: "#116B98",
  desertSun: "#E27D3D",
  charcoal: "#0A0A0A",
  midGray: "#6E6E6E",
  lightGray: "#F4F5F4",
  champagne: "#F7E7CE",
  white: "#FFFFFF",
};

// ============================================================================
// ANIMATION VARIANTS
// ============================================================================
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

// ============================================================================
// DATA
// ============================================================================
const categories = [
  { id: "tutorials", label: "Tutorials", icon: BookOpen, color: colors.blueGrotto },
  { id: "case-studies", label: "Case Studies", icon: TrendingUp, color: colors.primary },
  { id: "product-updates", label: "Product Updates", icon: Lightbulb, color: colors.desertSun },
  { id: "company-news", label: "Company News", icon: Building2, color: colors.secondary },
  { id: "agriculture", label: "Agriculture", icon: Sprout, color: colors.primary },
  { id: "brewing", label: "Brewing", icon: Beer, color: colors.blueGrotto },
  { id: "restaurants", label: "Restaurants", icon: UtensilsCrossed, color: colors.desertSun },
  { id: "iot", label: "IoT", icon: Cpu, color: colors.seafoam },
];

const placeholderPosts = [
  {
    id: "1",
    title: "Getting Started with IoT Sensors",
    excerpt: "Learn the fundamentals of connecting and configuring IoT sensors for your operation. From setup to monitoring, we cover everything you need to know.",
    category: "Tutorials",
    categoryColor: colors.blueGrotto,
    readTime: "5 min read",
    gradientFrom: colors.primary,
    gradientTo: colors.seafoam,
  },
  {
    id: "2",
    title: "How Smart Irrigation Saved 30% Water",
    excerpt: "A California vineyard's journey to sustainable water management with TerraMatrix. Real results from real operations.",
    category: "Case Studies",
    categoryColor: colors.primary,
    readTime: "8 min read",
    gradientFrom: colors.blueGrotto,
    gradientTo: colors.seafoam,
  },
  {
    id: "3",
    title: "Introducing Real-Time Alerts 2.0",
    excerpt: "Our latest update brings smarter notifications and customizable alert thresholds. Stay informed without the noise.",
    category: "Product Updates",
    categoryColor: colors.desertSun,
    readTime: "3 min read",
    gradientFrom: colors.desertSun,
    gradientTo: colors.champagne,
  },
];

const footerLinks = {
  product: [
    { label: "Features", href: "/#features" },
    { label: "Solutions", href: "/#industries" },
    { label: "Pricing", href: "#" },
    { label: "Documentation", href: "#" },
  ],
  company: [
    { label: "About", href: "/about" },
    { label: "Blog", href: "/blog" },
    { label: "Careers", href: "/careers" },
    { label: "Contact", href: "/contact" },
  ],
  legal: [
    { label: "Privacy Policy", href: "#" },
    { label: "Terms of Service", href: "#" },
    { label: "Cookie Policy", href: "#" },
  ],
};

// ============================================================================
// NAVIGATION COMPONENT
// ============================================================================
interface NavigationProps {
  scrolled: boolean;
  onContactClick: () => void;
}

const Navigation: React.FC<NavigationProps> = ({ scrolled, onContactClick }) => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [signInModalOpen, setSignInModalOpen] = useState(false);
  const { isMobile } = useResponsiveInfo();

  const handleSignInClick = () => {
    if (isMobile) {
      navigate("/login");
    } else {
      setSignInModalOpen(true);
    }
    setMobileMenuOpen(false);
  };

  const handleContactClick = () => {
    setMobileMenuOpen(false);
    onContactClick();
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "backdrop-blur-md shadow-sm" : ""
      }`}
      style={{
        backgroundColor: scrolled ? "rgba(255, 255, 255, 0.95)" : "transparent",
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-[60px]">
          {/* Logo - Left */}
          <div className="flex-1 flex items-center">
            <a href="/" className="flex-shrink-0">
              <TerraMatrixBrand
                markClassName="h-8 w-8 md:h-10 md:w-10"
                nameClassName="text-xl md:text-2xl text-gray-950"
                className="transition-all duration-300"
              />
            </a>
          </div>

          {/* Desktop Navigation - Center */}
          <div className="hidden md:flex items-center justify-center gap-6">
            <Button
              variant="ghost"
              className="font-medium transition-colors duration-200 hover:bg-gray-100"
              style={{ color: colors.midGray }}
              onClick={() => navigate("/")}
            >
              Home
            </Button>
            <Button
              variant="ghost"
              className="font-medium transition-colors duration-200 hover:bg-gray-100"
              style={{ color: colors.midGray }}
              onClick={() => navigate("/about")}
            >
              About
            </Button>
            <Button
              variant="ghost"
              className="font-medium transition-colors duration-200 hover:bg-gray-100"
              style={{ color: colors.primary, fontWeight: 600 }}
              onClick={() => navigate("/blog")}
            >
              Blog
            </Button>
            <Button
              variant="ghost"
              className="font-medium transition-colors duration-200 hover:bg-gray-100"
              style={{ color: colors.midGray }}
              onClick={handleContactClick}
            >
              Contact
            </Button>
          </div>

          {/* Desktop Sign In - Right */}
          <div className="flex-1 hidden md:flex items-center justify-end">
            <Button
              className="font-medium rounded transition-all duration-200 bg-primary hover:bg-primary/90 text-primary-foreground"
              onClick={handleSignInClick}
            >
              Sign In
            </Button>
          </div>

          {/* Mobile menu button */}
          <button
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? (
              <X className="text-gray-900" size={24} />
            ) : (
              <Menu className="text-gray-900" size={24} />
            )}
          </button>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="md:hidden bg-white rounded-lg shadow-lg p-4 mb-4"
          >
            <div className="flex flex-col gap-3">
              <Button
                variant="ghost"
                className="w-full justify-start transition-colors duration-200"
                style={{ color: colors.charcoal }}
                onClick={() => {
                  setMobileMenuOpen(false);
                  navigate("/");
                }}
              >
                Home
              </Button>
              <Button
                variant="ghost"
                className="w-full justify-start transition-colors duration-200"
                style={{ color: colors.charcoal }}
                onClick={() => {
                  setMobileMenuOpen(false);
                  navigate("/about");
                }}
              >
                About
              </Button>
              <Button
                variant="ghost"
                className="w-full justify-start transition-colors duration-200"
                style={{ color: colors.primary, fontWeight: 600 }}
                onClick={() => {
                  setMobileMenuOpen(false);
                  navigate("/blog");
                }}
              >
                Blog
              </Button>
              <Button
                variant="ghost"
                className="w-full justify-start transition-colors duration-200"
                style={{ color: colors.charcoal }}
                onClick={handleContactClick}
              >
                Contact
              </Button>
              <hr className="border-gray-200" />
              <Button
                variant="ghost"
                className="w-full justify-start transition-colors duration-200"
                style={{ color: colors.charcoal }}
                onClick={handleSignInClick}
              >
                Sign In
              </Button>
            </div>
          </motion.div>
        )}
      </div>

      {/* Sign In Modal */}
      <SignInModal
        isOpen={signInModalOpen}
        onClose={() => setSignInModalOpen(false)}
      />
    </nav>
  );
};

// ============================================================================
// HERO SECTION
// ============================================================================
const HeroSection: React.FC = () => {
  return (
    <section
      className="pt-32 pb-16 md:pt-40 md:pb-20"
      style={{ backgroundColor: colors.champagne }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
          className="text-center max-w-3xl mx-auto"
        >
          <motion.div
            variants={fadeInUp}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6"
            style={{ backgroundColor: `${colors.primary}15` }}
          >
            <span
              className="text-sm font-medium"
              style={{ color: colors.primary }}
            >
              Coming Soon
            </span>
          </motion.div>

          <motion.h1
            variants={fadeInUp}
            transition={{ duration: 0.6 }}
            className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6"
            style={{ color: colors.charcoal }}
          >
            TerraMatrix Blog
          </motion.h1>

          <motion.p
            variants={fadeInUp}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-lg md:text-xl mb-8 max-w-2xl mx-auto leading-relaxed"
            style={{ color: colors.midGray }}
          >
            Insights, guides, and stories from the world of IoT and automation.
            Learn how leading operations are transforming their workflows.
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
};

// ============================================================================
// CATEGORIES SECTION
// ============================================================================
const CategoriesSection: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  return (
    <section className="py-12 bg-white border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
        >
          <motion.h2
            variants={fadeInUp}
            className="text-sm font-semibold uppercase tracking-wider mb-6 text-center"
            style={{ color: colors.midGray }}
          >
            Browse by Topic
          </motion.h2>

          <motion.div
            variants={fadeInUp}
            className="flex flex-wrap justify-center gap-3"
          >
            {categories.map((category) => {
              const Icon = category.icon;
              const isActive = activeCategory === category.id;

              return (
                <button
                  key={category.id}
                  onClick={() =>
                    setActiveCategory(isActive ? null : category.id)
                  }
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 border ${
                    isActive
                      ? "shadow-md"
                      : "hover:shadow-sm"
                  }`}
                  style={{
                    backgroundColor: isActive ? category.color : colors.white,
                    color: isActive ? colors.white : category.color,
                    borderColor: isActive ? category.color : `${category.color}40`,
                  }}
                >
                  <Icon size={16} />
                  {category.label}
                </button>
              );
            })}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// ============================================================================
// BLOG POST CARD COMPONENT
// ============================================================================
interface BlogPostCardProps {
  post: typeof placeholderPosts[0];
  index: number;
}

const BlogPostCard: React.FC<BlogPostCardProps> = ({ post, index }) => {
  return (
    <motion.article
      variants={fadeInUp}
      transition={{ delay: index * 0.1 }}
      whileHover={{ y: -8, transition: { duration: 0.2 } }}
      className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100"
    >
      {/* Gradient Image Placeholder */}
      <div
        className="relative h-48 md:h-56"
        style={{
          background: `linear-gradient(135deg, ${post.gradientFrom} 0%, ${post.gradientTo} 100%)`,
        }}
      >
        {/* Coming Soon Badge */}
        <div className="absolute top-4 right-4">
          <span
            className="px-3 py-1 text-xs font-semibold rounded-full"
            style={{
              backgroundColor: "rgba(255, 255, 255, 0.9)",
              color: colors.charcoal,
            }}
          >
            Coming Soon
          </span>
        </div>

        {/* Category Badge */}
        <div className="absolute top-4 left-4">
          <span
            className="px-3 py-1 text-xs font-semibold rounded-full text-white"
            style={{ backgroundColor: post.categoryColor }}
          >
            {post.category}
          </span>
        </div>

        {/* Decorative Pattern */}
        <div className="absolute inset-0 opacity-10">
          <svg
            className="w-full h-full"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
          >
            <pattern
              id={`grid-${post.id}`}
              width="10"
              height="10"
              patternUnits="userSpaceOnUse"
            >
              <circle cx="1" cy="1" r="1" fill="white" />
            </pattern>
            <rect width="100" height="100" fill={`url(#grid-${post.id})`} />
          </svg>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        <h3
          className="text-xl font-semibold mb-3 line-clamp-2 group-hover:text-primary transition-colors"
          style={{ color: colors.charcoal }}
        >
          {post.title}
        </h3>

        <p
          className="text-sm leading-relaxed mb-4 line-clamp-2"
          style={{ color: colors.midGray }}
        >
          {post.excerpt}
        </p>

        <div
          className="flex items-center gap-4 text-sm"
          style={{ color: colors.midGray }}
        >
          <span className="flex items-center gap-1">
            <Clock size={14} />
            {post.readTime}
          </span>
        </div>
      </div>
    </motion.article>
  );
};

// ============================================================================
// BLOG GRID SECTION
// ============================================================================
const BlogGridSection: React.FC = () => {
  return (
    <section className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
        >
          <motion.div variants={fadeInUp} className="text-center mb-12">
            <h2
              className="text-3xl md:text-4xl font-bold mb-4"
              style={{ color: colors.charcoal }}
            >
              Latest Articles
            </h2>
            <p
              className="text-lg max-w-2xl mx-auto"
              style={{ color: colors.midGray }}
            >
              Stay tuned for in-depth guides, success stories, and product updates.
            </p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {placeholderPosts.map((post, index) => (
              <BlogPostCard key={post.id} post={post} index={index} />
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// ============================================================================
// NEWSLETTER SECTION
// ============================================================================
const NewsletterSection: React.FC = () => {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [message, setMessage] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      setStatus("error");
      setMessage("Please enter your email address");
      return;
    }

    setStatus("loading");

    try {
      const result = await newsletterService.subscribe(email);
      if (result.success) {
        setStatus("success");
        setMessage(result.message || "You're subscribed! We'll keep you updated.");
        setEmail("");
      } else {
        setStatus("error");
        setMessage(result.message || "Something went wrong. Please try again.");
      }
    } catch {
      setStatus("error");
      setMessage("Something went wrong. Please try again.");
    }
  };

  return (
    <section className="py-16 md:py-24" style={{ backgroundColor: colors.lightGray }}>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
          className="text-center"
        >
          <motion.div
            variants={fadeInUp}
            className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-6"
            style={{ backgroundColor: `${colors.primary}15` }}
          >
            <Mail className="h-8 w-8" style={{ color: colors.primary }} />
          </motion.div>

          <motion.h2
            variants={fadeInUp}
            className="text-3xl md:text-4xl font-bold mb-4"
            style={{ color: colors.charcoal }}
          >
            Be the First to Know
          </motion.h2>

          <motion.p
            variants={fadeInUp}
            className="text-lg mb-8 max-w-xl mx-auto"
            style={{ color: colors.midGray }}
          >
            Subscribe to get notified when we publish new articles, guides, and product updates.
          </motion.p>

          <motion.form
            variants={fadeInUp}
            onSubmit={handleSubmit}
            className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto"
          >
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              disabled={status === "loading" || status === "success"}
              className="flex-1 px-4 py-3 rounded-lg border border-input focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed"
              aria-label="Email address"
            />
            <Button
              type="submit"
              disabled={status === "loading" || status === "success"}
              className="px-6 py-3 font-semibold rounded-lg transition-all duration-200 bg-primary hover:bg-primary/90 text-primary-foreground disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {status === "loading" ? "Subscribing..." : status === "success" ? "Subscribed!" : "Subscribe"}
            </Button>
          </motion.form>

          {/* Status Message */}
          {(status === "success" || status === "error") && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`flex items-center justify-center gap-2 mt-4 text-sm ${
                status === "success" ? "text-green-600" : "text-red-600"
              }`}
            >
              {status === "success" ? (
                <CheckCircle size={16} />
              ) : (
                <AlertCircle size={16} />
              )}
              {message}
            </motion.div>
          )}

          <motion.p
            variants={fadeInUp}
            className="text-sm mt-4"
            style={{ color: colors.midGray }}
          >
            No spam, unsubscribe anytime.
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
};

// ============================================================================
// CTA SECTION
// ============================================================================
interface CTASectionProps {
  onContactClick: () => void;
  onDemoClick: () => void;
}

const CTASection: React.FC<CTASectionProps> = ({ onContactClick, onDemoClick }) => {
  return (
    <section className="py-20 md:py-28" style={{ backgroundColor: colors.primary }}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
        >
          <motion.h2
            variants={fadeInUp}
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6"
          >
            Ready to Transform Your Operations?
          </motion.h2>
          <motion.p
            variants={fadeInUp}
            className="text-lg md:text-xl text-white/90 mb-10 max-w-2xl mx-auto"
          >
            See how TerraMatrix can help you monitor, automate, and optimize your workflows
            with AI-powered IoT.
          </motion.p>
          <motion.div
            variants={fadeInUp}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Button
              size="lg"
              className="text-base font-semibold min-w-[180px] px-8 py-6 rounded border-2 shadow-lg hover:shadow-xl bg-background hover:bg-accent border-background hover:border-accent text-primary transition-all duration-200 hover:scale-105"
              onClick={onContactClick}
            >
              Contact
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button
              size="lg"
              variant="ghost"
              className="text-base font-semibold min-w-[180px] px-8 py-6 rounded border-2 border-white text-white hover:bg-white/20 hover:text-white transition-all duration-200"
              onClick={onDemoClick}
            >
              Watch Demo
            </Button>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// ============================================================================
// FOOTER SECTION
// ============================================================================
const FooterSection: React.FC = () => {
  return (
    <footer className="py-16 md:py-20 text-white" style={{ backgroundColor: colors.charcoal }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-12 mb-12">
          {/* Brand */}
          <div className="lg:col-span-2">
            <TerraMatrixBrand
              markClassName="h-10 w-10"
              nameClassName="text-3xl text-white"
              className="mb-6"
            />
            <p className="text-gray-400 max-w-sm leading-relaxed mb-6">
              The operating system for real-world infrastructure. Monitor, automate, and optimize
              your operations with AI-powered IoT.
            </p>
            <div className="flex gap-4">
              <a
                href="https://twitter.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center hover:bg-gray-700 transition-colors"
                aria-label="Twitter"
              >
                <Twitter className="h-5 w-5" />
              </a>
              <a
                href="https://linkedin.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center hover:bg-gray-700 transition-colors"
                aria-label="LinkedIn"
              >
                <Linkedin className="h-5 w-5" />
              </a>
            </div>
          </div>

          {/* Product Links */}
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-400 mb-4">Product</h3>
            <ul className="space-y-3">
              {footerLinks.product.map((link) => (
                <li key={link.label}>
                  <a href={link.href} className="text-gray-300 hover:text-white transition-colors">
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Links */}
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-400 mb-4">Company</h3>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.label}>
                  <a href={link.href} className="text-gray-300 hover:text-white transition-colors">
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-400 mb-4">Legal</h3>
            <ul className="space-y-3">
              {footerLinks.legal.map((link) => (
                <li key={link.label}>
                  <a href={link.href} className="text-gray-300 hover:text-white transition-colors">
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="pt-8 border-t border-gray-800 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-400 text-sm">
            &copy; {new Date().getFullYear()} TerraMatrix. All rights reserved.
          </p>
          <p className="text-gray-500 text-sm">Built with precision for real-world operations.</p>
        </div>
      </div>
    </footer>
  );
};

// ============================================================================
// MAIN BLOG PAGE COMPONENT
// ============================================================================
export const BlogPage: React.FC = () => {
  const navigate = useNavigate();
  const { isMobile } = useResponsiveInfo();

  // Scroll state
  const [scrolled, setScrolled] = useState(false);

  // Modal states
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [demoModalOpen, setDemoModalOpen] = useState(false);
  const [thankYouModalOpen, setThankYouModalOpen] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState("");

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Handle contact button click
  const handleContactClick = () => {
    if (isMobile) {
      navigate("/contact");
    } else {
      setContactModalOpen(true);
    }
  };

  // Handle successful form submission
  const handleContactSuccess = (email: string) => {
    setSubmittedEmail(email);
    setContactModalOpen(false);
    setThankYouModalOpen(true);
  };

  // Handle thank you modal close
  const handleThankYouClose = () => {
    setThankYouModalOpen(false);
    setSubmittedEmail("");
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="TerraMatrix Blog - IoT & Automation Insights"
        description="Explore guides, case studies, and product updates from TerraMatrix. Learn how leading farms, breweries, and restaurants are transforming operations with IoT sensor analytics and AI automation."
        canonical="/blog"
        keywords="IoT blog, agriculture technology articles, brewing automation guides, restaurant analytics insights, sensor monitoring best practices"
      />
      {/* Skip to main content link for accessibility */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-background focus:text-primary focus:rounded focus:shadow-lg"
      >
        Skip to main content
      </a>

      <Navigation scrolled={scrolled} onContactClick={handleContactClick} />

      <main id="main-content">
        <HeroSection />
        <CategoriesSection />
        <BlogGridSection />
        <NewsletterSection />
        <CTASection
          onContactClick={handleContactClick}
          onDemoClick={() => setDemoModalOpen(true)}
        />
      </main>

      <FooterSection />

      {/* Contact Modal */}
      <PilotContactModal
        isOpen={contactModalOpen}
        onClose={() => setContactModalOpen(false)}
        onSuccess={handleContactSuccess}
      />

      {/* Thank You Modal */}
      <ThankYouModal
        isOpen={thankYouModalOpen}
        onClose={handleThankYouClose}
        email={submittedEmail}
      />

      {/* Demo Modal */}
      <DemoModal
        isOpen={demoModalOpen}
        onClose={() => setDemoModalOpen(false)}
      />
    </div>
  );
};
